import {
    Alert,
    Avatar,
    Box,
    CircularProgress,
    Divider,
    Link,
    Snackbar,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import mixpanel from 'mixpanel-browser';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import GlobalContext from '../../contexts/GlobalContext';
import MigrateToExistingAccountContext, {
    EMigrateToExistingAccountUiState,
} from '../../contexts/MigrateToExistingAccountContext';
import { IconFamily2 } from '../../icons/Icons';
import { EMixpanelEvent } from '../../util/constants';
import { getInitials } from '../../util/helpers';
import PasscodeInput from '../PasscodeInput/PasscodeInput';
import ScreenHeader from '../ScreenHeader/ScreenHeader';

enum ELocalUiState {
    ready,
    loading,
    error,
}

const MigrateToExistingAccountForm: FC = () => {
    const { language } = useContext(GlobalContext);
    const { custodian, verifyAndCreateDependent, resendVerificationCode, setMigrateToExistingAccountUiState } =
        useContext(MigrateToExistingAccountContext);
    const theme = useTheme();
    const [localUiState, setLocalUiState] = useState(ELocalUiState.ready);
    const [errorMessage, setErrorMessage] = useState('');
    const [passcodeGuid, setPasscodeGuid] = useState(Math.random());
    const [disablePasscode, setDisablePasscode] = useState(false);
    const [showToaster, setShowToaster] = useState(false);
    const initials = useMemo(() => getInitials(custodian.firstName, custodian.lastName), [custodian]);

    const handleSubmitVerificationCode = async (verificationCode: string) => {
        setLocalUiState(ELocalUiState.loading);
        setDisablePasscode(true);
        try {
            await verifyAndCreateDependent(verificationCode);
            mixpanel.track(EMixpanelEvent.actionCompleted, { key: 'consumer_migration_existing_account' });
            setMigrateToExistingAccountUiState(EMigrateToExistingAccountUiState.success);
        } catch (error) {
            if (error.cause === 400) {
                setErrorMessage(language.createAccount.passcode.error.invalid);
                setPasscodeGuid(Math.random());
            } else {
                setErrorMessage(language.createAccount.passcode.error.generic);
            }
            mixpanel.track(EMixpanelEvent.actionFailed, { key: 'consumer_migration_existing_account' });
            setDisablePasscode(false);
            setLocalUiState(ELocalUiState.error);
        }
    };

    const handleResendPasscode = async () => {
        try {
            await resendVerificationCode();
            mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_migration_existing_account_resend_passcode' });
            toggleToaster();
        } catch (error) {
            if (error.cause === 429) {
                setErrorMessage(language.createAccount.passcode.error.locked);
            } else {
                setErrorMessage(language.createAccount.passcode.error.generic);
            }
            setLocalUiState(ELocalUiState.error);
        }
    };

    const toggleToaster = () => {
        setShowToaster(true);
        setTimeout(() => {
            setShowToaster(false);
        }, 5000);
    };

    useEffect(() => {
        mixpanel.track(EMixpanelEvent.viewed, { key: 'consumer_migration_existing_account_verify' });
    }, []);

    return (
        <>
            <Box sx={{ textAlign: { lg: 'center' } }}>
                <IconFamily2 fillColor={theme.palette.primary.main} />
            </Box>
            <ScreenHeader title={language.migrateToExistingAccount.verify.title} />
            <Stack direction={'row'} alignItems={'center'} data-testid="primary-user">
                <Avatar
                    sx={{
                        background: theme.palette.primary.main,
                        mr: 2,
                        width: '30px',
                        height: '30px',
                        fontSize: '14px',
                    }}
                >
                    {initials}
                </Avatar>
                <Box>
                    <Typography
                        component={'p'}
                        variant={'subtitle2'}
                    >{`${custodian.firstName} ${custodian.lastName}`}</Typography>
                    <Typography component={'p'} variant={'body2'}>{`${custodian.email}`}</Typography>
                </Box>
            </Stack>
            <Box sx={{ my: 3 }}>
                <Typography component={'span'} variant={'body2'}>
                    {language.migrateToExistingAccount.verify.desc} <b>{custodian.email}</b>
                </Typography>
            </Box>
            <Divider sx={{ my: 3 }} />
            {localUiState === ELocalUiState.loading && (
                <Stack direction={'row'} sx={{ justifyContent: 'center' }}>
                    <CircularProgress size={20} />
                </Stack>
            )}
            {localUiState === ELocalUiState.error && (
                <Alert severity="error" sx={{ my: 2 }}>
                    {errorMessage}
                </Alert>
            )}
            <form data-testid="verify-form">
                <Box sx={{ my: 4 }}>
                    <PasscodeInput
                        key={passcodeGuid}
                        onSubmit={handleSubmitVerificationCode}
                        isDisabled={disablePasscode}
                    />
                </Box>
            </form>
            <Typography component={'p'} variant={'body2'}>
                {language.migrateToExistingAccount.verify.disclaimer}
            </Typography>
            <Box sx={{ my: 2 }}>
                <Link
                    sx={{ typography: 'subtitle2', display: 'block' }}
                    color={theme.palette.text.primary}
                    component={'button'}
                    type="button"
                    onClick={handleResendPasscode}
                >
                    {language.createAccount.passcode.cta.resendPasscode}
                </Link>
            </Box>
            <Snackbar open={showToaster}>
                <Alert severity="success">{language.verify.sendSuccess}</Alert>
            </Snackbar>
        </>
    );
};

export default MigrateToExistingAccountForm;
